import React, { useContext, useState } from 'react';
import { Alert, Box, Button, CircularProgress, DialogContent, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { DeliveryState } from '../../../enums/orders';
import { useParams } from 'react-router-dom';
import { showDeliveryState, showPaymentState } from '../../common/styles/order-states.style';
import { cardTableStyle } from '../../common/styles/grid-card.style';
import axios, { AxiosResponse } from 'axios';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { AuthContext } from '../../../contexts/user-context/user-context';
import ClearIcon from '@mui/icons-material/Clear';

import { API_ENDPOINT } from '../../../configurations/global.config';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import InvoicePDFDialog from '../../common/invoice-pdf-dialog/invoice-pdf-dialog';
import CreateOrderForm from '../orders/create-order-form/create-order-form';
import ChangeStateForm from './change_state_form';
import { isMobile, isTablet } from 'react-device-detect';
import { parseOrderEventActor, parseOrderEventChanges } from '../../../utils/orderevent';
import { DriverInvoiceState, IDriverInvoice } from '../../../interfaces/common/driver-invoices';
import { IMerchantOrdersInvoice, MerchantOrdersInvoiceState } from '../../../interfaces/common/merchant-invoices';
import OrderTimeline from './order-timeline';
import { IconWithText } from '../../common/styles/components';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import { DataResources, ResourceActionType, user_has_data_model_access, user_is_admin } from '../../../utils/permissions';
import OrderNotes from '../../common/order-notes/order-notes';
import { AcUnit, Info, SyncAlt } from '@mui/icons-material';
import { showOrderLabel } from '../../common/styles/order-labels';
import { useSnackBar } from '../../../contexts/snackbar';
import { format } from 'date-fns';
import FreezeOrderDialog from '../../common/frozen-orders/freeze-order-dialog';
import UnfreezeOrderDialog from '../../common/frozen-orders/unfreeze-order-dialog';
import { IOrderDeliveryTask } from '../../../interfaces/delivery-company/tasks';
import RestoreOrderButton from '../../common/restore-order-button/restore-order-button';
import { InvoiceMetaState } from '../../../interfaces/common/invoice';
import UndoDeliveryDialog from '../../common/undo-delivery-dialog/undo-delivery-dialog';
import LebanonMapFlow from '../../common/lebanon-map/lebanon-map';
import OrderDetailsWithMap from '../../common/order-page-utils/order-details-with-map';
import { 
  OrderAction, 
  createEditAction, 
  createChangeStateAction, 
  createCancelAction,
  createUndoDeliveryAction,
  createRerouteAction,
} from '../../common/order-page-utils/order-actions-header';
import RerouteOrderDialog from '../../common/reroute-order-dialog';
import { useSelectedBranch } from '../../../contexts/selected-branch-context';
import { canEditItem } from '../../../utils/branch-permissions';
import BranchIndicator from '../../common/branch-indicator/branch-indicator';
import {
  getPackageDescription,
  renderPackageDetailsRows,
} from '../../../utils/order';

const Order = () => {
  const { orderId } = useParams();
  console.log(orderId);
  const [order, setOrder] = React.useState<OrderType>();
  const [confirmDeleteOrder, setConfirmDeleteOrder] =
    React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [response, setResponse] = React.useState<AxiosResponse>();
  const [message, setMessage] = React.useState<string>('');
  const [invoiceData, setInvoiceData] =
    React.useState<IMerchantOrdersInvoice | null>(null);
  const [lockedInvoice, setLockedInvoice] = React.useState<
    IDriverInvoice | IMerchantOrdersInvoice | null
  >(null);
  const { user, config } = React.useContext(AuthContext);
  const { selectedBranch } = useSelectedBranch();

  const { color } = useContext(OrgSettingsContext);
  const [confirmUninvoiceOrder, setConfirmUninvoiceOrder] = useState(false);
  const [invoiceLockOperation, setInvoiceLockOperation] = useState<
    'view' | 'delete' | null
  >(null);

  const [editingOrder, setEditingOrder] = useState(false);
  const [updatingDeliveryState, setUpdatingDeliveryState] = useState(false);

  const [undoDeliveryDialogOpen, setUndoDeliveryDialogOpen] = useState(false);
  const [rerouteDialogOpen, setRerouteDialogOpen] = useState(false);
 
  const { showMessage } = useSnackBar();
  const updateDeliveryTask = (t: IOrderDeliveryTask) => {
    if (order && order.delivery_tasks) {
      const tasks = order.delivery_tasks.map(item => item.id != t.id ? item : t);

      setOrder({
        ...order,
        delivery_tasks: tasks,
      });
    }

  };

  const getAndSetOrder = async () => {
    if (orderId !== undefined) {
      setIsLoading(true);

      try {
        console.log('will fetch now');
        const endpoint = `${API_ENDPOINT}/api/v1/order/${orderId}/`;
        console.log(endpoint);
        const response = await axios.get<OrderType>(endpoint, config);
        console.log(response);
        setResponse(response);
        setOrder(response.data);
        console.log(order);
      } catch (e) {
        setOrder(undefined);
        if (axios.isAxiosError(e)) {
          setMessage('Failed to load order data: ' + e.response?.statusText);
          setResponse(e.response);
        } else {
          setMessage('Failed to load order data');
        }
        console.log(e);
      }
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    console.log('use effect');
    getAndSetOrder();
  }, [orderId]);


  const canDeleteOrder = user_has_data_model_access(user, DataResources.ORDER, ResourceActionType.DELETE);

  const canUpdateOrder = user_has_data_model_access(user, DataResources.ORDER, ResourceActionType.UPDATE);

  const deleteOrder = async (reason?: string) => {
    if (order && order.id !== '') {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/api/v1/order/${order.id}/?reason=${encodeURIComponent(reason ?? 'not specified')}`,
          config,
        );
        setResponse(response);

        setOrder(undefined);
        showMessage('Order cancelled successfully', 'success');
        setOrder({ ...order, delivery_state: DeliveryState.CANCELLED });
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          showMessage('Failed to cancel order', 'error');
        } else {
          setMessage('Failed to cancel order');
        }
        console.log(e);
      }

      setIsLoading(false);
    }
  };

  const uninvoiceOrder = async () => {
    if (order) {
      setInvoiceLockOperation('delete');
      if (order.merchant_invoice) setLockedInvoice(order.merchant_invoice);
      else if (order.driver_invoice) setLockedInvoice(order.driver_invoice);
      const endpoint = `${API_ENDPOINT}/api/v1/order/uninvoice/${encodeURIComponent(order?.id ?? '')}/`;
      try {
        await axios.put(endpoint, {}, config);
        if (order.merchant_invoice) {
          setOrder({ ...order, merchant_invoice: undefined });
        } else {
          setOrder({ ...order, driver_invoice: undefined });
        }
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to uninvoice order: ' + e.response?.statusText);
        } else {
          setMessage('Failed to uninvoice order');
        }
      }
      setInvoiceLockOperation(null);
      setLockedInvoice(null);
    }
  };

  const resetOrderState =
    (state: DeliveryState, reason: string) => async () => {
      if (order) {
        setIsLoading(true);
        setResponse(undefined);
        try {
          const response = await axios.patch(
            `${API_ENDPOINT}/api/v1/order/${order.id}/`,
            { delivery_state: state, reason: reason },
            config,
          );
          setResponse(response);
          setOrder({ ...order, delivery_state: state });
          showMessage("Order updated.", "success")
        } catch (e) {
          if (axios.isAxiosError(e)) {
            setResponse(e.response);
            setMessage('Failed to update order: ' + e.response?.statusText);
          } else {
            setMessage('Failed to update order');
          }
          console.log(e);
        }

        setIsLoading(false);
      }
    };

  /** Build actions for the order header */
  const buildOrderActions = (): { primary: OrderAction[], secondary: OrderAction[] } => {
    if (!order) return { primary: [], secondary: [] };

    // Check if user can edit this order based on branch
    const canEditBasedOnBranch = canEditItem(order.branch, selectedBranch);
    
    // If order belongs to another branch, return empty actions
    if (!canEditBasedOnBranch) {
      return { primary: [], secondary: [] };
    }

    const isCancelled = order.delivery_state === DeliveryState.CANCELLED;
    const isDelivered = [DeliveryState.DELIVERED, DeliveryState.DELIVERED_AT_WAREHOUSE].includes(order.delivery_state);
    const canEditOrCancel = ![DeliveryState.CANCELLED, DeliveryState.DELIVERED, DeliveryState.DELIVERED_AT_WAREHOUSE].includes(order.delivery_state);

    const primary: OrderAction[] = [];
    const secondary: OrderAction[] = [];

    // Edit Order - Primary action when available
    if (canEditOrCancel) {
      primary.push(createEditAction(canUpdateOrder, () => setEditingOrder(true)));
    }

    // Change Delivery State - Primary action when available
    if (canEditOrCancel) {
      primary.push(createChangeStateAction(canUpdateOrder, () => setUpdatingDeliveryState(true)));
    }

    // Reroute - Available when order is at warehouse
    if (order.delivery_state === DeliveryState.AT_WAREHOUSE) {
      primary.push(createRerouteAction(
        () => setRerouteDialogOpen(true),
        canUpdateOrder,
        order.delivery_state,
        order.next_leg 
          ? `Order is scheduled for transfer to ${order.next_leg.destination?.name}. Click to change routing.`
          : 'Change how this order is routed for delivery'
      ));
    }

    // Cancel Order - Secondary action
    if (canEditOrCancel) {
      secondary.push(createCancelAction(canDeleteOrder, () => setConfirmDeleteOrder(true)));
    }

    // Undo Delivery - Secondary action (admin only)
    if (isDelivered && user_is_admin(user)) {
      const isPaidToMerchant = order.merchant_invoice?.status === MerchantOrdersInvoiceState.PAID;
      const tooltipText = isPaidToMerchant 
        ? 'Order is already paid to merchant. You cannot undo delivery.'
        : order.delivery_state === DeliveryState.DELIVERED_AT_WAREHOUSE 
          ? 'Order will be put back in warehouse, and removed from all invoices.'
          : 'Order will be marked as IN TRANSIT again, and will be removed from all invoices.';

      secondary.push(createUndoDeliveryAction(
        () => setUndoDeliveryDialogOpen(true),
        isPaidToMerchant,
        tooltipText
      ));
    }

    return { primary, secondary };
  };

  const fetchInvoiceContent = async (
    invoice: IMerchantOrdersInvoice | null,
  ) => {
    if (!invoice || !order) return;
    setLockedInvoice(invoice);
    setInvoiceLockOperation('view');
    console.log('fetching invoice: ', invoice);

    const endpoint = `${API_ENDPOINT}/api/v1/merchant-invoice/${invoice.id}/`;
    console.log(endpoint);
    try {
      const response = await axios.get(endpoint, config);
      setResponse(response);
      console.log('fetched invoice: ', response);
      const data: IMerchantOrdersInvoice = response.data;
      setInvoiceData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to download invoice content: ' + e.response?.statusText,
        );
      } else {
        setMessage('Failed to download invoice content');
      }
    }
    setLockedInvoice(null);
    setInvoiceLockOperation(null);
  };

  

  return (
    <div>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          {
            <>
              {message != '' && (
                <>
                  <Alert
                    severity={
                      response &&
                      response.status >= 200 &&
                      response.status < 300
                        ? 'success'
                        : 'error'
                    }
                  >
                    <>
                      {message}

                      {response && response.status >= 300 && (
                        <ErrorsFlattenner rawErrors={response.data} />
                      )}
                    </>
                  </Alert>
                  <br />{' '}
                </>
              )}

              {!order ? (
                <Alert severity="error">
                  An unexpected error occurred, please contact us to resolve the
                  issue.
                </Alert>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      pb: 0,
                      borderRadius: '12px',
                      gap: 1,
                    }}
                  >
                    {!canEditItem(order.branch, selectedBranch) && (
                      <Box sx={{ width: '100%', maxWidth: 800 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Read-Only Mode:</strong> This order belongs to branch &quot;{order.branch?.name}&quot;. 
                            You are currently viewing from branch &quot;{selectedBranch?.name}&quot;. 
                            All action buttons are disabled. Switch to branch &quot;{order.branch?.name}&quot; to modify this order.
                          </Typography>
                        </Alert>
                      </Box>
                    )}
                    {(() => {
                      const actions = buildOrderActions();
                      const canEditBasedOnBranch = canEditItem(order.branch, selectedBranch);
                      return (
                        <OrderDetailsWithMap
                          order={order}
                          setOrder={setOrder}
                          additionalPrimaryActions={actions.primary}
                          additionalSecondaryActions={actions.secondary}
                          canEditBasedOnBranch={canEditBasedOnBranch}
                        />
                      );
                    })()}


                    <ConfirmAction
                      message={'Are you sure you want to cancel this order?'}
                      requireReason
                      action={deleteOrder}
                      isOpen={confirmDeleteOrder}
                      isSevereAction
                      handleClose={() => setConfirmDeleteOrder(false)}
                    >
                      <Alert severity="warning">
                        This action can not be undone.
                      </Alert>
                    </ConfirmAction>

                    <ConfirmAction
                      message={'Are you sure you want to uninvoice this order?'}
                      action={uninvoiceOrder}
                      isOpen={confirmUninvoiceOrder}
                      handleClose={() => setConfirmUninvoiceOrder(false)}
                    >
                      <div>Attention: This action can not be undone</div>
                    </ConfirmAction>

                    <CustomDialog
                      fullWidth
                      open={updatingDeliveryState}
                      onClose={() => setUpdatingDeliveryState(false)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          paddingLeft: 20,
                          width: '100%',
                        }}
                      >
                        <Typography
                          variant="h5"
                          component="h5"
                          sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                        >
                          Change Delivery State
                        </Typography>

                        <IconButton
                          onClick={() => setUpdatingDeliveryState(false)}
                        >
                          <ClearIcon color="primary" fontSize="large" />
                        </IconButton>
                      </div>

                      <DialogContent>
                        {order && (
                          <ChangeStateForm
                            orderData={order}
                            onSubmit={resetOrderState}
                            onClose={() => setUpdatingDeliveryState(false)}
                          />
                        )}
                      </DialogContent>
                    </CustomDialog>

                    <CustomDialog
                      fullWidth
                      maxWidth="sm"
                      open={editingOrder}
                      onClose={() => setEditingOrder(false)}
                      fullScreen={isMobile && !isTablet}
                    >
                      <div
                        style={{
                          textAlign: 'right',
                          width: '100%',
                        }}
                      >
                        <IconButton onClick={() => setEditingOrder(false)}>
                          <ClearIcon color="primary" fontSize="large" />
                        </IconButton>
                      </div>

                      <DialogContent>
                        <CreateOrderForm defaultOrder={order} onSuccess={setOrder} />
                      </DialogContent>
                    </CustomDialog>

                    {invoiceData !== null && (
                      <InvoicePDFDialog
                        pdfData={invoiceData}
                        onClose={() => {
                          setInvoiceData(null);
                        }}
                      />
                    )}
                  </Box>

                  <Box mt={2}>
                    <OrderTimeline
                      order={order}
                      statestamps={order.statestamps}
                      events={order.events}
                      color={color}
                      showDeliveryState={showDeliveryState}
                      showPaymentState={showPaymentState}
                      parseOrderEventActor={parseOrderEventActor}
                      parseOrderEventChanges={parseOrderEventChanges}
                      pickup_tasks={order.pickup_tasks ?? []}
                      delivery_tasks={order.delivery_tasks ?? []}
                      updateDeliveryTask={updateDeliveryTask}
                    />
                  </Box>
                </>
              )}
            </>
          }
        </>
      )}
      <UndoDeliveryDialog
        open={undoDeliveryDialogOpen}
        onClose={() => {
          setUndoDeliveryDialogOpen(false);
        }}
        order={order}
        onSuccess={() => {
          showMessage('Delivery undone successfully');
          getAndSetOrder();
          setUndoDeliveryDialogOpen(false);
        }}
      />
      
      {order && (
        <RerouteOrderDialog
          orders={[order]}
          open={rerouteDialogOpen}
          onClose={() => setRerouteDialogOpen(false)}
          onSuccess={() => {
            showMessage('Order rerouted successfully');
            getAndSetOrder();
          }}
        />
      )}
    </div>
  );
};

export default Order;
